<template>
    <main class="flex-1 bg-gray-50">
        <div class="py-6">
            <!-- Header Section -->
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            Správa kategórií článkov
                        </h2>
                        <p class="mt-1 text-sm text-gray-500">
                            Spravujte kategórie pre organizáciu článkov
                        </p>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <button @click="showAddModal = true" type="button"
                            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                            <svg class="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none"
                                viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            Pridať kategóriu
                        </button>
                    </div>
                </div>
            </div>

            <!-- Main Content -->
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                <!-- Categories Table -->
                <div class="bg-white shadow rounded-lg">
                    <div class="px-6 py-4 border-b border-gray-200">
                        <h3 class="text-lg leading-6 font-medium text-gray-900">Zoznam kategórií</h3>
                    </div>

                    <div class="overflow-hidden">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ID
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Názov
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Popis
                                    </th>
                                    <th scope="col"
                                        class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Počet článkov
                                    </th>
                                    <th scope="col" class="relative px-6 py-3">
                                        <span class="sr-only">Akcie</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-200">
                                <tr v-for="category in categories" :key="category.id" class="hover:bg-gray-50">
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {{ category.id }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap">
                                        <div class="text-sm font-medium text-gray-900">{{ category.name }}</div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <div class="text-sm text-gray-500 max-w-xs truncate">{{ category.description }}
                                        </div>
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {{ category.posts_count || 0 }}
                                    </td>
                                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button @click="editCategory(category)"
                                            class="text-teal-600 hover:text-teal-900 mr-4">
                                            Upraviť
                                        </button>
                                        <button @click="deleteCategory(category)"
                                            class="text-red-600 hover:text-red-900"
                                            :disabled="(category.posts_count ?? 0) > 0">
                                            Zmazať
                                        </button>
                                    </td>
                                </tr>
                                <tr v-if="categories.length === 0">
                                    <td colspan="5" class="px-6 py-4 text-center text-sm text-gray-500">
                                        Žiadne kategórie neboli nájdené
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add/Edit Modal -->
        <div v-if="showAddModal || showEditModal"
            class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div class="mt-3">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">
                        {{ showEditModal ? 'Upraviť kategóriu' : 'Pridať novú kategóriu' }}
                    </h3>

                    <form @submit.prevent="saveCategory" class="space-y-4">
                        <div>
                            <label for="name" class="block text-sm font-medium text-gray-700">
                                Názov kategórie <span class="text-red-500">*</span>
                            </label>
                            <input type="text" id="name" v-model="currentCategory.name"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                placeholder="Zadajte názov kategórie" required>
                        </div>

                        <div>
                            <label for="description" class="block text-sm font-medium text-gray-700">
                                Popis
                            </label>
                            <textarea id="description" v-model="currentCategory.description" rows="3"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
                                placeholder="Zadajte popis kategórie"></textarea>
                        </div>

                        <div class="flex justify-end space-x-3 pt-4">
                            <button type="button" @click="closeModal"
                                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                                Zrušiť
                            </button>
                            <button type="submit" :disabled="isSubmitting"
                                class="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed">
                                <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none"
                                    viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                                {{ isSubmitting ? 'Ukladám...' : (showEditModal ? 'Uložiť' : 'Pridať') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </main>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PostCategoryDataService from '../services/PostCategoryDataService'
import type { PostCategory } from '../services/PostCategoryDataService'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

export default defineComponent({
    name: 'post-category-list',

    data() {
        return {
            categories: [] as PostCategory[],
            showAddModal: false,
            showEditModal: false,
            isSubmitting: false,
            currentCategory: {
                name: '',
                description: ''
            } as PostCategory
        }
    },

    async mounted() {
        await this.loadCategories()
    },

    methods: {
        async loadCategories() {
            try {
                const response = await PostCategoryDataService.getAll()
                // Fix: Categories are nested in response.data.data
                this.categories = (response.data as any)?.data || response.data
            } catch (error: unknown) {
                console.error('Error loading categories:', error)
                const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
                toast.error('Chyba pri načítavaní kategórií: ' + errorMessage)
            }
        },

        editCategory(category: PostCategory) {
            this.currentCategory = { ...category }
            this.showEditModal = true
        }, async saveCategory() {
            if (!this.currentCategory.name?.trim()) {
                toast.error('Názov kategórie je povinný')
                return
            }

            this.isSubmitting = true

            try {
                if (this.showEditModal && this.currentCategory.id) {
                    await PostCategoryDataService.update(this.currentCategory.id, this.currentCategory)
                    toast.success('Kategória bola úspešne upravená!')
                } else {
                    await PostCategoryDataService.add(this.currentCategory)
                    toast.success('Kategória bola úspešne pridaná!')
                }

                await this.loadCategories()
                this.closeModal()
            } catch (error: unknown) {
                console.error('Error saving category:', error)
                const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
                toast.error('Chyba pri ukladaní kategórie: ' + errorMessage)
            } finally {
                this.isSubmitting = false
            }
        },

        async deleteCategory(category: PostCategory) {
            if (category.posts_count && category.posts_count > 0) {
                toast.error('Nemôžete zmazať kategóriu, ktorá obsahuje články')
                return
            }

            if (!confirm(`Naozaj chcete zmazať kategóriu "${category.name}"?`)) {
                return
            } try {
                if (category.id) {
                    await PostCategoryDataService.delete(category.id)
                }
                toast.success('Kategória bola úspešne zmazaná!')
                await this.loadCategories()
            } catch (error: unknown) {
                console.error('Error deleting category:', error)
                const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
                toast.error('Chyba pri mazaní kategórie: ' + errorMessage)
            }
        },

        closeModal() {
            this.showAddModal = false
            this.showEditModal = false
            this.currentCategory = {
                name: '',
                description: ''
            }
        }
    }
})
</script>
